import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, FileText, Check, X, Calendar, Building2, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";

interface Document {
  id: string;
  title: string;
  file_name: string;
  file_path: string;
  mime_type: string | null;
  bucket_id: string;
  doc_type: string;
  issuer_name: string | null;
  doc_date: string | null;
  expires_at: string | null;
  ai_summary: string | null;
  category: string | null;
  key_fields: any;
  created_at: string;
}

interface DocumentLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskLabel: string;
  onSelect: (document: Document) => void;
  suggestedKeywords?: string[];
}

const DOC_TYPE_LABELS: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  tax: "Impôts",
  insurance: "Assurance",
  identity: "Identité",
  receipt: "Reçu",
  quote: "Devis",
  payslip: "Bulletin de salaire",
  attestation: "Attestation",
  bank_statement: "Relevé bancaire",
  medical_certificate: "Certificat médical",
  notarial_deed: "Acte notarié",
  administrative: "Administratif",
  warranty: "Garantie",
  other: "Autre",
};

export function DocumentLinkDialog({
  open,
  onOpenChange,
  taskLabel,
  onSelect,
  suggestedKeywords = [],
}: DocumentLinkDialogProps) {
  const { user } = useAuth();
  const { openDocument } = useDocumentPreview();
  const [searchQuery, setSearchQuery] = useState("");

  const handlePreview = useCallback((doc: Document) => {
    openDocument(doc as unknown as Tables<"documents">);
  }, [openDocument]);

  // Reset search when opening
  useEffect(() => {
    if (open) {
      // Pre-fill search with keywords from task label
      const keywords = extractKeywords(taskLabel);
      setSearchQuery(keywords.join(" "));
    }
  }, [open, taskLabel]);

  const { data: documents, isLoading } = useQuery({
    queryKey: ["documents-for-linking", searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("documents")
        .select("id, title, file_name, file_path, mime_type, bucket_id, doc_type, issuer_name, doc_date, expires_at, ai_summary, category, key_fields, created_at")
        .eq("user_id", user?.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (searchQuery.trim()) {
        // Simple text search on title
        query = query.ilike("title", `%${searchQuery.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Document[];
    },
    enabled: !!user && open,
  });

  // Extract keywords from task label for smart suggestions
  const extractKeywords = (label: string): string[] => {
    const commonWords = new Set([
      "de", "du", "des", "le", "la", "les", "un", "une", "et", "ou", "à", "en",
      "pour", "par", "sur", "avec", "dans", "attestation", "certificat", "document",
      "pièce", "justificatif", "copie", "original"
    ]);
    
    return label
      .toLowerCase()
      .split(/\s+/)
      .filter(word => word.length > 2 && !commonWords.has(word))
      .slice(0, 3);
  };

  // Score documents based on relevance to task label
  const scoreDocument = (doc: Document): number => {
    const labelLower = taskLabel.toLowerCase();
    const titleLower = doc.title.toLowerCase();
    const issuerLower = (doc.issuer_name || "").toLowerCase();
    const docType = doc.doc_type;

    let score = 0;

    // Title match
    if (titleLower.includes(labelLower) || labelLower.includes(titleLower)) {
      score += 50;
    }

    // Keyword matches
    const keywords = extractKeywords(taskLabel);
    keywords.forEach(keyword => {
      if (titleLower.includes(keyword)) score += 20;
      if (issuerLower.includes(keyword)) score += 15;
    });

    // Document type relevance
    const typeMapping: Record<string, string[]> = {
      attestation: ["attestation", "certificat", "justificatif"],
      insurance: ["assurance", "garantie", "police"],
      tax: ["impôt", "fiscal", "taxe", "tva"],
      bank_statement: ["banque", "rib", "bancaire", "compte"],
      identity: ["identité", "carte", "passeport", "kbis"],
      contract: ["contrat", "convention", "accord"],
      invoice: ["facture", "paiement"],
    };

    Object.entries(typeMapping).forEach(([type, words]) => {
      if (docType === type && words.some(w => labelLower.includes(w))) {
        score += 30;
      }
    });

    // Recency bonus
    const daysSinceCreation = (Date.now() - new Date(doc.created_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceCreation < 30) score += 10;
    if (daysSinceCreation < 7) score += 5;

    return score;
  };

  // Sort documents by relevance
  const sortedDocuments = documents
    ?.map(doc => ({ ...doc, score: scoreDocument(doc) }))
    .sort((a, b) => b.score - a.score);

  const suggestedDocs = sortedDocuments?.filter(d => d.score > 20).slice(0, 3);
  const otherDocs = sortedDocuments?.filter(d => !suggestedDocs?.includes(d));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Lier un document</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            Pour : <span className="font-medium text-foreground">{taskLabel}</span>
          </p>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un document..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Results */}
        <ScrollArea className="h-[300px] -mx-6 px-6">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Suggested matches */}
              {suggestedDocs && suggestedDocs.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-emerald-500" />
                    Correspondances suggérées
                  </p>
                  <div className="space-y-1.5">
                    {suggestedDocs.map((doc) => (
                      <DocumentRow 
                        key={doc.id} 
                        document={doc} 
                        onSelect={() => onSelect(doc)}
                        onPreview={() => handlePreview(doc)}
                        highlighted
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Other documents */}
              {otherDocs && otherDocs.length > 0 && (
                <div>
                  {suggestedDocs && suggestedDocs.length > 0 && (
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Autres documents
                    </p>
                  )}
                  <div className="space-y-1.5">
                    {otherDocs.map((doc) => (
                      <DocumentRow 
                        key={doc.id} 
                        document={doc} 
                        onSelect={() => onSelect(doc)}
                        onPreview={() => handlePreview(doc)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(!sortedDocuments || sortedDocuments.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun document trouvé</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface DocumentRowProps {
  document: Document;
  onSelect: () => void;
  onPreview: () => void;
  highlighted?: boolean;
}

function DocumentRow({ document, onSelect, onPreview, highlighted }: DocumentRowProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
        highlighted 
          ? "bg-emerald-50 hover:bg-emerald-100 border border-emerald-200" 
          : "hover:bg-muted/50 border border-transparent"
      )}
    >
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <FileText className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{document.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {DOC_TYPE_LABELS[document.doc_type] || document.doc_type}
          </Badge>
          {document.issuer_name && (
            <span className="flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3" />
              {document.issuer_name}
            </span>
          )}
          {document.doc_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(new Date(document.doc_date), "MMM yyyy", { locale: fr })}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
        className="p-1.5 rounded-md hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground transition-colors"
        title="Prévisualiser"
      >
        <Eye className="w-4 h-4" />
      </button>
      {highlighted && (
        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
      )}
    </button>
  );
}
